import type React from 'react';
import { useEffect, useState } from 'react';
import { apiUrl, fetchWithAuth } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { formatEuro } from '../utils/currency';

interface DashboardMetrics {
  totalIncome: number;
  totalExpenses: number;
  netAmount: number;
  transactionCount: number;
  budgetStatus: 'on_track' | 'over_budget' | 'under_budget' | 'no_budget';
  recentTransactions: {
    id: number;
    date: string;
    description: string;
    amount: number;
    type: 'deposit' | 'payment';
  }[];
  costpointBreakdown: {
    costpoint: string;
    income: number;
    expenses: number;
    net: number;
  }[];
  monthlyTrend: {
    month: string;
    income: number;
    expenses: number;
    net: number;
  }[];
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getBudgetStatusText = (status: DashboardMetrics['budgetStatus']) => {
    switch (status) {
      case 'on_track':
        return 'On Track';
      case 'under_budget':
        return 'Under Budget';
      case 'over_budget':
        return 'Over Budget';
      case 'no_budget':
        return 'No Budget';
      default:
        return 'Unknown';
    }
  };

  const getBudgetStatusColor = (status: DashboardMetrics['budgetStatus']) => {
    switch (status) {
      case 'on_track':
        return 'text-green-600';
      case 'under_budget':
        return 'text-blue-600';
      case 'over_budget':
        return 'text-red-600';
      case 'no_budget':
        return 'text-gray-600';
      default:
        return 'text-gray-600';
    }
  };

  // Fetch dashboard metrics
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetchWithAuth(apiUrl('dashboard/metrics'));
        if (response.ok) {
          const data = await response.json();
          setMetrics(data);
        } else {
          setError('Failed to load dashboard data');
        }
      } catch (err) {
        console.error('Error fetching dashboard metrics:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
          Welcome back, {user?.fullName || user?.username}!
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          Overview of your financial data and key metrics
        </p>
      </div>

      {/* Role-based welcome message */}
      <div className="mt-6 bg-blue-50 border-l-4 border-blue-400 p-4">
        <div className="flex">
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              {user?.role === 'superadmin' && 'You have full system administration access.'}
              {user?.role === 'admin' && 'You can manage users and all system features.'}
              {user?.role === 'budget_creator' && 'You can create and manage budgets.'}
              {user?.role === 'operator' && 'You can import transactions and create budgets.'}
              {user?.role === 'viewer' && 'You have read-only access to view financial data.'}
            </p>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="mt-8 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-sm text-gray-600">Loading dashboard data...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mt-8 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Metrics */}
      {!loading && !error && metrics && (
        <>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm font-medium">€</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Income</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {formatEuro(metrics.totalIncome)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm font-medium">€</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Expenses</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {formatEuro(metrics.totalExpenses)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm font-medium">📊</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Transactions</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {metrics.transactionCount}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm font-medium">📈</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Budget Status</dt>
                      <dd
                        className={`text-lg font-medium ${getBudgetStatusColor(
                          metrics.budgetStatus
                        )}`}
                      >
                        {getBudgetStatusText(metrics.budgetStatus)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Dashboard Sections */}
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Recent Transactions */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Recent Transactions
                </h3>
                {metrics.recentTransactions.length === 0 ? (
                  <p className="text-sm text-gray-500">No transactions yet</p>
                ) : (
                  <div className="space-y-3">
                    {metrics.recentTransactions.slice(0, 5).map((transaction) => (
                      <div key={transaction.id} className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {transaction.description}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(transaction.date).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="ml-4 flex-shrink-0">
                          <span
                            className={`text-sm font-medium ${
                              transaction.type === 'deposit' ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {transaction.type === 'deposit' ? '+' : '-'}
                            {formatEuro(Math.abs(transaction.amount))}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Costpoint Breakdown */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Costpoint Breakdown
                </h3>
                {metrics.costpointBreakdown.length === 0 ? (
                  <p className="text-sm text-gray-500">No costpoint data available</p>
                ) : (
                  <div className="space-y-3">
                    {metrics.costpointBreakdown.map((costpoint) => (
                      <div key={costpoint.costpoint} className="border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-gray-900 uppercase">
                            {costpoint.costpoint}
                          </h4>
                          <span
                            className={`text-sm font-medium ${
                              costpoint.net >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            Net: {formatEuro(costpoint.net)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div>
                            <span className="text-gray-500">Income: </span>
                            <span className="text-green-600">{formatEuro(costpoint.income)}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Expenses: </span>
                            <span className="text-red-600">{formatEuro(costpoint.expenses)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Net Amount Summary */}
          <div className="mt-6">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Net Amount (Current Year)
                  </h3>
                  <div className="text-right">
                    <div
                      className={`text-2xl font-bold ${
                        metrics.netAmount >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {formatEuro(metrics.netAmount)}
                    </div>
                    <p className="text-sm text-gray-500">
                      {metrics.netAmount >= 0 ? 'Profit' : 'Loss'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
